<template>
  <component
    :is="inline ? 'section' : 'gs-drawer'"
    :class="{ 'gs-promo-settings-inline': inline }"
    v-model="open"
    size="wide"
    :close-label="$t('frontend_cancel')"
    :title="mode === 'create'
      ? $t('frontend_resources_index_create_new_funnel')
      : $t('frontend_groovesell_edit_funnel_promo_tools')"
    :subtitle="localModel.name"
    @close="modalClosed()"
  >
    <div class="gs-promo-drawer">
      <nav class="gs-vtabs" aria-label="Promo tools settings sections">
        <button
          v-for="tab in addFunnelResourceTabs"
          :key="tab.value"
          type="button"
          class="gs-vtabs__item"
          :class="{ 'is-active': currentAddFunnelResourceTab === tab.value }"
          :aria-current="currentAddFunnelResourceTab === tab.value ? 'true' : 'false'"
          @click="handleAddFunnelResourceTabChange(tab.value)"
        >
          {{ tab.title }}
        </button>
      </nav>

      <div class="gs-promo-drawer__panel">
        <template v-if="currentAddFunnelResourceTab === 'info'">
          <gs-field :label="$t('frontend_resources_name')">
            <text-input
              v-model="localModel.name"
              :validator="$v.localModel.name"
              :attribute="$t('frontend_resources_campaign_name')"
              value="localModel.name"
              :placeholder="$t('frontend_resources_campaign_name')"
            />
          </gs-field>

          <gs-field :label="$t('frontend_resources_default_landing_page')">
            <gs-name-select
              v-if="funnel && funnel.main_product && funnel.main_product.affiliate_landing_pages"
              v-model="localModel.landing_page_id"
              size="full"
              max-height="220px"
              :options="landingPagesOptions"
              :placeholder="$t('frontend_resources_default_landing_page')"
            />
          </gs-field>

          <div class="gs-switch-row">
            <span class="gs-switch-row__text">
              <span class="gs-switch-row__label">{{ $t('frontend_resources_custom_tutorial_video') }}</span>
              <span class="gs-switch-row__help">
                Show your own walkthrough at the top of the portal instead of the default one.
              </span>
            </span>
            <gd-toggle
              v-model="localModel.use_custom_video"
              :value="localModel.use_custom_video"
            />
          </div>

          <gs-field
            v-if="localModel.use_custom_video"
            :label="$t('frontend_resources_custom_video_link')"
          >
            <text-input
              v-model="localModel.video_link"
              :validator="$v.localModel.video_link"
              :attribute="$t('frontend_resources_custom_video_link')"
              value="localModel.video_link"
              :placeholder="$t('frontend_resources_custom_video_link')"
            />
          </gs-field>

          <div class="gs-switch-row">
            <span class="gs-switch-row__text">
              <span class="gs-switch-row__label">{{ $t('frontend_resources_show_related_funnels') }}</span>
              <span class="gs-switch-row__help">
                Point affiliates at your other funnels from inside this portal.
              </span>
            </span>
            <gd-toggle
              v-model="localModel.show_more_funnels"
              :value="localModel.show_more_funnels"
            />
          </div>

          <gs-field
            v-if="localModel.show_more_funnels"
            :label="$t('frontend_resources_related_funnels')"
          >
            <multi-select
              v-model="localModel.related_funnels"
              track-by="id"
              label="name"
              :class="{ 'invalid': $v.localModel.related_funnels.$error }"
              :placeholder="$t('frontend_affiliate_settings_select_one')"
              :searchable="false"
              :multiple="true"
              :allow-empty="false"
              :options="relatedFunnelsList"
            >
            </multi-select>
            <span v-if="$v.localModel.related_funnels.$error" class="gs-promo-drawer__error">
              {{ $t('please_select_atleast_one_funnel') }}
            </span>
          </gs-field>

          <!-- The create endpoint does not persist show_more_resources /
               related_resources, so the row only shows once the promo tools exist. -->
          <div v-if="mode === 'edit'" class="gs-switch-row">
            <span class="gs-switch-row__text">
              <span class="gs-switch-row__label">{{ $t('frontend_resources_is_blocked_resources') }}</span>
              <span class="gs-switch-row__help">
                List your other affiliate portals so affiliates can promote more than one funnel.
              </span>
            </span>
            <gd-toggle
              v-model="localModel.show_more_resources"
              :value="localModel.show_more_resources"
            />
          </div>

          <gs-field
            v-if="mode === 'edit' && localModel.show_more_resources"
            :label="$t('frontend_resources_blocked_funnel_resources')"
          >
            <multi-select
              v-model="localModel.related_resources"
              track-by="id"
              label="name"
              :class="{ 'invalid': $v.localModel.related_resources.$error }"
              :placeholder="$t('frontend_resources_blocked_funnel_resources')"
              :searchable="false"
              :multiple="true"
              :allow-empty="false"
              :options="otherResources"
            >
            </multi-select>
            <span v-if="$v.localModel.related_resources.$error" class="gs-promo-drawer__error">
              {{ $t('please_select_atleast_one_funnel_resource') }}
            </span>
          </gs-field>
        </template>

        <template v-else-if="currentAddFunnelResourceTab === 'links'">
          <p class="gs-promo-drawer__intro">
            Drag to set the order affiliates see these landing pages in, and switch off any you do not want
            them sending traffic to. New pages are on by default; affiliate assignments still control who can see each page.
          </p>

          <draggable
            v-model="localModel.landing_pages"
            handle=".gs-promo-drawer__grip"
            :scroll-sensitivity="200"
            tag="ul"
            class="gs-promo-drawer__links"
          >
            <li
              v-for="landingPage in localModel.landing_pages"
              :key="landingPage.id"
              class="gs-promo-drawer__link-row"
            >
              <span class="gs-promo-drawer__grip" aria-hidden="true">
                <gs-icon name="grip-vertical" size="16" />
              </span>
              <div class="gs-promo-drawer__link-name">
                <span>{{ landingPage.description }}</span>
                <div class="gs-promo-drawer__audience">
                  <span v-if="isPublicPage(landingPage)">Available to all affiliates</span>
                  <template v-else>
                    <span>{{ pageAffiliates(landingPage).length ? 'Only showing to these affiliates' : 'No affiliates selected' }}</span>
                    <affiliate-pills :affiliates="pageAffiliates(landingPage)" expandable />
                  </template>
                </div>
              </div>
              <gd-toggle
                v-model="landingPage.enabled"
                :value="landingPage.enabled"
              />
            </li>
          </draggable>

          <p v-if="!localModel.landing_pages || !localModel.landing_pages.length" class="gs-promo-drawer__empty">
            This funnel has no affiliate landing pages yet. Add them to the funnel's main product first.
          </p>
        </template>

        <template v-else>
          <gs-field :label="$t('frontend_header_text')">
            <text-input
              v-model="localModel.header_text"
              :validator="$v.localModel.header_text"
              :attribute="$t('frontend_header_text')"
              value="localModel.header_text"
              :placeholder="$t('frontend_header_text')"
            />
          </gs-field>

          <gs-field :label="$t('frontend_signup_text')">
            <text-input
              v-model="localModel.signup_text"
              :validator="$v.localModel.signup_text"
              :attribute="$t('frontend_signup_text')"
              value="localModel.signup_text"
              :placeholder="$t('frontend_signup_text')"
            />
          </gs-field>

          <gs-field :label="$t('frontend_login_text')">
            <text-input
              v-model="localModel.login_text"
              :validator="$v.localModel.login_text"
              :attribute="$t('frontend_login_text')"
              value="localModel.login_text"
              :placeholder="$t('frontend_login_text')"
            />
          </gs-field>

          <!-- The create endpoint does not persist disable_branding, so the row
               only shows once the promo tools exist. -->
          <div v-if="mode === 'edit'" class="gs-switch-row">
            <span class="gs-switch-row__text">
              <span class="gs-switch-row__label">Disable &quot;Powered by&quot; branding</span>
              <span class="gs-switch-row__help">
                Hide the platform credit at the bottom of the affiliate portal.
              </span>
            </span>
            <gd-toggle
              v-model="localModel.disable_branding"
              :value="localModel.disable_branding"
            />
          </div>

          <div class="gs-promo-drawer__preview">
            <span class="gs-promo-drawer__preview-label">{{ $t('frontend_preview') }}</span>
            <p class="gs-promo-drawer__preview-header">{{ localModel.header_text }}</p>
            <div class="gs-promo-drawer__preview-pills">
              <span class="gs-promo-drawer__pill">{{ localModel.login_text }}</span>
              <span class="gs-promo-drawer__pill is-primary">{{ localModel.signup_text }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div v-if="!inline" class="gs-promo-drawer__footer">
      <gs-button variant="outline" @click="close()">
        {{ $t('frontend_cancel') }}
      </gs-button>
      <gs-button variant="primary" @click="submit()">
        {{ submitLabel }}
      </gs-button>
    </div>
  </component>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
  requiredIf,
} from 'vuelidate/lib/validators';
import getVideoId from 'get-video-id';
import router from '@/router';
import draggable from 'vuedraggable';
import AffiliatePills from '../../products/steps/tabs/affiliates/AffiliatePills.vue';

export default {
  name: 'PromoAreaSettingsDrawer',
  components: {
    draggable,
    AffiliatePills,
  },
  props: {
    inline: { type: Boolean, default: false },
    model: {
      type: Object,
      required: true,
    },
    funnel: {
      type: Object,
    },
    funnels: {
      type: Array,
      required: true,
    },
    resource: {
      type: Object,
    },
    resources: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localModel: this.model,
      addFunnelResourceTabs: [
        { title: this.$t('frontend_groovesell_tools_affiliate_modal_tabs_info'), value: 'info' },
        { title: this.$t('frontend_vendors_links'), value: 'links' },
        { title: this.$t('branding'), value: 'branding' },
      ],
      currentAddFunnelResourceTab: 'info',
      open: false,
      mode: 'edit',
    }
  },
  computed: {
    submitLabel() {
      return this.mode === 'create'
        ? 'Create & add assets'
        : this.$t('frontend_resource_index_update');
    },
    landingPagesOptions() {
      return this.funnel.main_product.affiliate_landing_pages.map((landingPage) => {
        return {
          value: landingPage.id,
          text: landingPage.description,
        };
      });
    },
    relatedFunnelsList() {
      const relatedFunnelsList = [];

      this.funnels.forEach((funnel) => {
        if (this.funnel.id != funnel.id) {
          relatedFunnelsList.push({ id: funnel.id, name: funnel.name });
        }
      });

      return relatedFunnelsList;
    },
    otherResources() {
      const otherResourcesList = [];

      this.resources.forEach((res) => {
        if (this.resource.id != res.id && res.is_active == 1) {
          otherResourcesList.push({ id: res.id, name: res.name });
        }
      });

      return otherResourcesList;
    },
  },
  created() {
    // The list page and the header card still open this editor by name through
    // the legacy root bus, so the drawer answers to the old modal names.
    this.$root.$on('modal-open', this.onRootOpen);
    this.$root.$on('modal-close', this.onRootClose);
  },
  beforeDestroy() {
    this.$root.$off('modal-open', this.onRootOpen);
    this.$root.$off('modal-close', this.onRootClose);
  },
  methods: {
    validateAndApply() {
      this.$v.localModel.$touch();
      if (this.$v.localModel.$invalid) return false;
      this.localModel.video_details = this.localModel.use_custom_video ? getVideoId(this.localModel.video_link) : {};
      return true;
    },
    pageAssignments(page) {
      const product = this.funnel && this.funnel.main_product;
      const source = ((product && product.affiliate_landing_pages) || []).find(item => String(item.id) === String(page.id));
      return (source ? source.assigned_to : page.assigned_to) || [];
    },
    isPublicPage(page) { return this.pageAssignments(page).some(affiliate => Number(affiliate.id) === 0); },
    pageAffiliates(page) { return this.pageAssignments(page).filter(affiliate => Number(affiliate.id) !== 0); },
    onRootOpen(name) {
      if (name === 'create-promo-area-modal') {
        this.mode = 'create';
        this.currentAddFunnelResourceTab = 'info';
        this.open = true;
      }

      if (name === 'edit-promo-area-modal') {
        this.mode = 'edit';
        this.currentAddFunnelResourceTab = 'info';
        this.open = true;
      }
    },
    onRootClose(name) {
      if (name === 'create-promo-area-modal' || name === 'edit-promo-area-modal') {
        this.open = false;
        this.modalClosed();
      }
    },
    close() {
      this.open = false;
      this.modalClosed();
    },
    submit() {
      return this.mode === 'create' ? this.createPromoArea() : this.editPromoArea();
    },
    modalClosed() {
      this.$v.$reset();
    },
    handleAddFunnelResourceTabChange(newTab) {
      this.currentAddFunnelResourceTab = newTab;
    },
    async createPromoArea() {
      this.$v.localModel.$touch();

      if (
        !this.$v.localModel.name.$invalid &&
        !this.$v.localModel.video_link.$invalid &&
        !this.$v.localModel.related_funnels.$invalid
      ) {
        this.$loader.start('createPromoArea');
        let resource = this.resource;

        resource.name = this.localModel.name;
        resource.landing_page_id = this.localModel.landing_page_id;
        resource.use_custom_video = this.localModel.use_custom_video;
        resource.video_details = getVideoId(this.localModel.video_link);
        resource.show_more_funnels = this.localModel.show_more_funnels;
        resource.related_funnels = this.localModel.related_funnels;
        resource.landing_pages = this.localModel.landing_pages;
        resource.header_text = this.localModel.header_text;
        resource.login_text = this.localModel.login_text;
        resource.signup_text = this.localModel.signup_text;
        const savedResource = await resource.save();
        this.$loader.end('createPromoArea');

        router.push({
          name: 'funnel_resource_assets',
          params: {
            id: savedResource.data.funnel_id,
            resource_id: savedResource.data.id,
          },
        });
      }
    },
    async editPromoArea() {
      this.$v.localModel.$touch();

      if (
        !this.$v.localModel.name.$invalid &&
        !this.$v.localModel.video_link.$invalid &&
        !this.$v.localModel.related_funnels.$invalid &&
        !this.$v.localModel.related_resources.$invalid
      ) {
        this.$loader.start('editPromoArea');
        let resources = this.resources;
        let resource = this.resource;

        resource.name = this.localModel.name;
        resource.landing_page_id = this.localModel.landing_page_id;
        resource.use_custom_video = this.localModel.use_custom_video;
        resource.video_details = getVideoId(this.localModel.video_link);
        resource.show_more_funnels = this.localModel.show_more_funnels;
        resource.related_funnels = this.localModel.related_funnels;
        resource.show_more_resources = this.localModel.show_more_resources;
        resource.related_resources = this.localModel.related_resources;
        resource.landing_pages = this.localModel.landing_pages;
        resource.header_text = this.localModel.header_text;
        resource.login_text = this.localModel.login_text;
        resource.signup_text = this.localModel.signup_text;
        resource.disable_branding = this.localModel.disable_branding;

        await resource.save();

        const editedResource = resources
          .map((item) => item.id)
          .indexOf(resource.id);

        resources[editedResource].name = resource.name;

        this.$emit('updateResources', resources);
        this.$emit('updateResource', resource);

        this.$root.$emit('modal-close', 'edit-promo-area-modal');
        this.$loader.end('editPromoArea');
      }
    },
  },
  validations: {
    localModel: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },
      video_link: {
        required: requiredIf((object) => {
          return object.use_custom_video;
        }),
        validVideoLink(value, object) {
          if (object.use_custom_video) {
            const videoDetails = getVideoId(value);
            if (videoDetails.id && videoDetails.service) {
              return true;
            }

            return false;
          }

          return true;
        },
      },
      related_funnels: {
        minimumNoOfFunnels(value, object) {
          if (object.show_more_funnels) {
            return value?.length > 0;
          }

          return true;
        },
      },
      related_resources: {
        minimumNoOfFunnelResources(value, object) {
          if (object.show_more_resources) {
            return value?.length > 0;
          }

          return true;
        },
      },
      header_text: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(200),
      },
      login_text: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(200),
      },
      signup_text: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(200),
      },
    },
  },
}
</script>

<style>
.invalid .multiselect__tags {
  border-color: red !important;
}
</style>

<style scoped>
.gs-promo-settings-inline { padding:24px; background:var(--gs-surface); border:1px solid var(--gs-border); border-radius:var(--gs-radius-lg); }
.gs-promo-settings-inline .gs-promo-drawer { grid-template-columns:140px minmax(0,1fr); gap:24px; }

.gs-promo-drawer {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}

.gs-vtabs {
  display: flex;
  position: sticky;
  top: 0;
  flex-direction: column;
  gap: 2px;
}

.gs-vtabs__item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
  text-align: left;
  background: transparent;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.gs-vtabs__item:hover {
  color: var(--gs-foreground);
  background: var(--gs-muted);
}

.gs-vtabs__item.is-active {
  color: var(--gs-foreground);
  font-weight: 700;
  background: var(--gs-primary-soft);
  border-left-color: var(--gs-primary);
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.gs-vtabs__item:focus {
  outline: none;
}

.gs-vtabs__item:focus-visible {
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-promo-drawer__panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

.gs-promo-drawer__intro {
  margin: 0;
  max-width: 62ch;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.55;
}

.gs-promo-drawer__error {
  display: block;
  margin-top: 6px;
  color: var(--gs-danger);
  font-size: 12px;
}

.gs-switch-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-switch-row__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-switch-row__label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-switch-row__help {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-promo-drawer__links {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-promo-drawer__link-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  background: var(--gs-surface);
}

.gs-promo-drawer__link-row + .gs-promo-drawer__link-row {
  border-top: 1px solid var(--gs-border);
}

.gs-promo-drawer__audience { display:flex; flex-direction:column; gap:6px; margin-top:6px; color:var(--gs-muted-foreground); font-size:12px; line-height:1.5; }

.gs-promo-drawer__grip {
  display: inline-flex;
  flex: none;
  align-items: center;
  color: var(--gs-muted-foreground);
  cursor: grab;
}

.gs-promo-drawer__link-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.4;
}

.gs-promo-drawer__empty {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.55;
}

.gs-promo-drawer__preview {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-promo-drawer__preview-label {
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.gs-promo-drawer__preview-header {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
}

.gs-promo-drawer__preview-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gs-promo-drawer__pill {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 4px 14px;
  overflow-wrap: anywhere;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border-strong);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.gs-promo-drawer__pill.is-primary {
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
  border-color: var(--gs-primary);
}

/* The kit's drawer body carries a 32px bottom padding, and a sticky element is
   clamped to its containing block — the body's *content* box — so the footer
   used to stop 32px short of the scroll area and the rows underneath it showed
   through that strip. The footer owns the bottom inset instead. */
.gs-drawer ::v-deep .gs-drawer__body {
  padding-bottom: 0;
}

.gs-promo-drawer__footer {
  display: flex;
  position: sticky;
  bottom: 0;
  z-index: 2;
  gap: 10px;
  justify-content: flex-end;
  margin: 24px -28px 0;
  padding: 16px 28px 24px;
  background: var(--gs-surface);
  border-top: 1px solid var(--gs-border);
}

@media (max-width: 860px) {
  .gs-promo-drawer {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
  }

  .gs-vtabs {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .gs-vtabs__item {
    width: auto;
  }
}

@media (max-width: 640px) {
  .gs-promo-drawer__footer {
    margin: 20px -18px 0;
    padding: 14px 18px 18px;
  }
}
</style>

<style scoped>
@media (max-width: 700px) { .gs-promo-settings-inline { padding:16px; } .gs-promo-settings-inline .gs-promo-drawer { grid-template-columns:minmax(0,1fr); gap:16px; } }
</style>
